/**
 * System → Login providers tab.
 *
 * Login provider management (CRUD, feature bindings, test-connection) for
 * administrators.
 */

import { useState, type ReactElement } from 'react';
import { isAxiosError } from 'axios';
import {
  useAdminLoginProviders,
  useCreateLoginProvider,
  useUpdateLoginProvider,
} from '@features/logins/hooks/useAdminLoginProviders';
import { ProviderTable } from '@features/logins/components/admin/ProviderTable';
import { ProviderFormDialog } from '@features/logins/components/admin/ProviderFormDialog';
import { BindingsDialog } from '@features/logins/components/admin/BindingsDialog';
import type { Provider, ProviderRequestBody } from '@features/logins/types';

function extractErrorDetail(error: unknown): string {
  if (isAxiosError(error)) {
    const data: unknown = error.response?.data;
    if (data && typeof data === 'object' && 'detail' in data) {
      const detail = (data as { detail: unknown }).detail;
      if (typeof detail === 'string') return detail;
    }
  }
  return 'Request failed. Please try again.';
}

export function LoginProvidersTab(): ReactElement {
  const { data: providers, isLoading, error } = useAdminLoginProviders();
  const createProvider = useCreateLoginProvider();
  const updateProvider = useUpdateLoginProvider();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [bindingsProvider, setBindingsProvider] = useState<Provider | null>(null);

  const handleEdit = (provider: Provider): void => {
    setSelectedProvider(provider);
    setFormError(null);
    setDialogOpen(true);
  };

  const handleCreate = (): void => {
    setSelectedProvider(null);
    setFormError(null);
    setDialogOpen(true);
  };

  const handleSubmit = (body: ProviderRequestBody): void => {
    setFormError(null);
    const onError = (err: unknown): void => { setFormError(extractErrorDetail(err)); };
    const onSuccess = (): void => { setDialogOpen(false); };
    if (selectedProvider) {
      updateProvider.mutate(
        { id: selectedProvider.id, body },
        { onSuccess, onError },
      );
    } else {
      createProvider.mutate(body, { onSuccess, onError });
    }
  };

  const handleDelete = (provider: Provider): void => {
    // No-op callback; deletion is handled by ProviderTable component.
    // TanStack Query automatically invalidates the providers query, so data refreshes.
    void provider; // Acknowledge parameter
  };

  if (isLoading) {
    return <div className="settings-loading">Loading login providers...</div>;
  }

  if (error) {
    return (
      <div className="settings-error">
        Failed to load login providers. Please refresh the page.
      </div>
    );
  }

  return (
    <>
      <div className="admin-page__header">
        <button
          type="button"
          className="admin-page__create-btn"
          onClick={handleCreate}
        >
          Create Provider
        </button>
      </div>

      <ProviderTable
        providers={providers ?? []}
        onEdit={handleEdit}
        onBindings={(provider) => { setBindingsProvider(provider); }}
        onDelete={handleDelete}
      />

      <ProviderFormDialog
        open={dialogOpen}
        provider={selectedProvider}
        onClose={() => { setDialogOpen(false); }}
        onSubmit={handleSubmit}
        isSubmitting={createProvider.isPending || updateProvider.isPending}
        submitError={formError}
      />

      <BindingsDialog
        open={bindingsProvider !== null}
        provider={bindingsProvider}
        onClose={() => { setBindingsProvider(null); }}
      />
    </>
  );
}
